import { useState } from "react";
import * as utils from "../../utils/utils";
import { useTheme } from "../Theme/ThemeContext";
import { useStore } from "../user/UserContext";
import Icon from "../Icon/Icon";
import { useNavigate } from "react-router-dom";
import { isAllowedFeature, isFreePlan, isUserSales, isUserTenant } from "../../utils/utils";
import packageJson from "../../../package.json";

interface sidebarProps {
  isSidebarOpen: boolean;
}
function SideNav({ isSidebarOpen }: sidebarProps) {
  const { store, setStore } = useStore();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const storeClone = { ...store };
  const uiSettings: any = storeClone.uiSettings ?? {};
  const [activeDropdown, setActiveDropdown] = useState<string | null>(
    uiSettings?.activeDropdown ?? null
  );
  // const [isAnySubmenuActive, setAnySubmenuActive] = useState<string | null>(
  //   uiSettings?.isAnySubmenuActive ?? null
  // );

  const MenuGroups = [
    {
      title: null,
      items: [
        {
          label: "Dashboard",
          icon: "fa-home",
          access: (store: any) =>
            utils.isUserSuperAdminOrAdmin(store) ||
            utils.isUserAccountAdmin(store) ||
            utils.isUserAccountUser(store) ||
            // Scoped by the API to their own properties.
            utils.isUserAgencyLandlord(store) ||
            utils.isUserTenant(store),
          path: "/dashboard"
        }
      ]
    },
    {
      title: "Admin",
      items: [
        {
          label: "Admin users",
          icon: "fa-user-tie",
          access: (store: any) => utils.isUserSuperAdmin(store),
          path: "/admin-users"
        },
        {
          label: "Agencies",
          icon: "fa-building",
          access: (store: any) => utils.isUserSuperAdminOrAdmin(store),
          path: "/agencies"
        },
        {
          label: "Agency owners / Landlords",
          icon: "fa-user-tie",
          // An agency is often a single independent landlord, so the heading
          // names both. Platform staff only: an agency has exactly one owner and
          // does not manage them from here.
          access: (store: any) => utils.isUserSuperAdminOrAdmin(store),
          path: "/landlords"
        },
        {
          label: "My Agency",
          icon: "fa-building",
          access: (store: any) => utils.isUserAccountAdmin(store),
          path: "/my-agency"
        },
        {
          label: "Landlords",
          icon: "fa-person",
          // The agency's own landlords — its clients, who own the properties it
          // manages. Managed by the agency, and by platform staff supporting it.
          access: (store: any) =>
            utils.isUserSuperAdminOrAdmin(store) || utils.isUserAgencyStaff(store),
          path: "/agency-landlords"
        },
        {
          label: "Agency account users",
          icon: "fa-person",
          access: (store: any) =>
            (utils.isUserSuperAdminOrAdmin(store) && !isUserSales(store)) ||
            (utils.isUserAccountAdmin(store) && !isFreePlan(store)),
          path: "/account-users"
        },
        {
          label: "Sales",
          icon: "fa-handshake",
          access: (store: any) => utils.isUserSuperAdminOrAdmin(store),
          path: "/sales-users"
        },
        {
          label: "Plans",
          icon: "fa-money",
          access: (store: any) => utils.isUserSuperAdmin(store),
          path: "/plans"
        },
        {
          label: "Roles",
          icon: "fa-people-group",
          access: (store: any) => utils.isUserSuperAdmin(store),
          path: `/roles`
        },
        {
          label: "Enterprise Onboarding",
          icon: "fa-star",
          access: (store: any) => utils.isUserSuperAdminOrAdmin(store),
          path: "/onboarding-submissions"
        }
      ]
    },
    {
      title: "Sales",
      items: [
        {
          label: "Sales Dashboard",
          icon: "fa-chart-line",
          access: (store: any) => utils.isUserSuperAdminOrAdmin(store) || isUserSales(store),
          path: "/sales-dashboard"
        },
        {
          label: "Sales Codes",
          icon: "fa-barcode",
          access: (store: any) => utils.isUserSuperAdminOrAdmin(store) || isUserSales(store),
          path: "/sales-codes"
        },
        {
          // Admin only: a trial code gives away paid product, so it is not
          // salespeople's to hand out. The API enforces the same rule.
          label: "Trial Codes",
          icon: "fa-ticket",
          access: (store: any) => utils.isUserSuperAdminOrAdmin(store),
          path: "/trial-codes"
        },
        {
          // Admin only for the same reason: a discount code cuts the price on
          // every renewal, not just the first invoice.
          label: "Discounts",
          icon: "fa-tags",
          access: (store: any) => utils.isUserSuperAdminOrAdmin(store),
          path: "/discounts"
        }
      ]
    },
    {
      title: "Property Management",
      items: [
        {
          label: "Property Groups",
          icon: "fa-city",
          access: (store: any) =>
            utils.isUserSuperAdminOrAdmin(store) ||
            isUserSales(store) ||
            utils.isUserAccountAdmin(store) ||
            utils.isUserAccountUser(store) ||
            // Scoped by the API to their own properties.
            utils.isUserAgencyLandlord(store) ||
            utils.isUserAgencyLandlord(store),
          path: "/property-group"
        },
        {
          label: "Properties",
          icon: "fa-building",
          access: (store: any) =>
            utils.isUserSuperAdminOrAdmin(store) ||
            isUserSales(store) ||
            utils.isUserAccountAdmin(store) ||
            utils.isUserAccountUser(store) ||
            // Scoped by the API to their own properties.
            utils.isUserAgencyLandlord(store) ||
            utils.isUserAgencyLandlord(store),
          path: "/properties"
        },
        {
          label: (
            <div>
              Rentals {!isAllowedFeature(store, "rentals-portal") ? <Icon name="fa-lock" /> : null}
            </div>
          ),
          icon: "fa-people-group",
          access: (store: any) =>
            utils.isUserSuperAdminOrAdmin(store) ||
            isUserSales(store) ||
            utils.isUserAccountAdmin(store) ||
            utils.isUserAccountUser(store) ||
            // Scoped by the API to their own properties.
            utils.isUserAgencyLandlord(store) ||
            utils.isUserTenant(store),
          path: "/rentals"
        },
        {
          label: (
            <div>
              Tenants {!isAllowedFeature(store, "tenants-portal") ? <Icon name="fa-lock" /> : null}
            </div>
          ),
          icon: "fa-bed",
          access: (store: any) =>
            utils.isUserSuperAdminOrAdmin(store) ||
            isUserSales(store) ||
            utils.isUserAccountAdmin(store) ||
            utils.isUserAccountUser(store) ||
            // Scoped by the API to their own properties.
            utils.isUserAgencyLandlord(store),
          path: "/tenants"
        },
        {
          label: (
            <div>
              Rental Applications{" "}
              {!isAllowedFeature(store, "rental-application-portal") ? (
                <Icon name="fa-lock" />
              ) : null}
            </div>
          ),
          icon: "fa-sticky-note",
          access: (store: any) =>
            utils.isUserSuperAdminOrAdmin(store) ||
            isUserSales(store) ||
            utils.isUserAccountAdmin(store) ||
            utils.isUserAccountUser(store) ||
            // Scoped by the API to their own properties.
            utils.isUserAgencyLandlord(store),
          path: "/rental-applications"
        },
        {
          label: "Lease Agreements",
          icon: "fa-file-signature",
          access: (store: any) =>
            utils.isUserSuperAdminOrAdmin(store) ||
            isUserSales(store) ||
            utils.isUserAccountAdmin(store) ||
            utils.isUserAccountUser(store) ||
            // Scoped by the API to their own properties.
            utils.isUserAgencyLandlord(store),
          path: "/lease-agreements"
        },
        {
          label: "Leads",
          icon: "fa-ear-listen",
          access: (store: any) =>
            utils.isUserSuperAdminOrAdmin(store) ||
            utils.isUserAccountAdmin(store) ||
            utils.isUserAccountUser(store) ||
            // Scoped by the API to their own properties.
            utils.isUserAgencyLandlord(store),
          path: "/leads"
        }
      ]
    },
    {
      title: null,
      items: [
        {
          label: "Payments",
          icon: "fa-money",
          access: (store: any) =>
            utils.isUserSuperAdminOrAdmin(store) ||
            isUserSales(store) ||
            // Tenants see what they have been billed. Not gated on the plan,
            // unlike the agency: the plan belongs to the agency, and a tenant
            // should not be locked out of their own bills because their landlord
            // is on the free tier.
            utils.isUserTenant(store) ||
            (utils.isUserLandlordOrAccountUser(store) && !isFreePlan(store)),
          path: "/payments"
        },
        {
          label: "Notifications",
          icon: "fa-bell",
          access: (store: any) =>
            utils.isUserSuperAdminOrAdmin(store) ||
            isUserSales(store) ||
            utils.isUserAccountAdmin(store) ||
            utils.isUserAccountUser(store) ||
            // Scoped by the API to their own properties.
            utils.isUserAgencyLandlord(store) ||
            utils.isUserTenant(store),
          path: "/notifications"
        },
        {
          label: (
            <div>
              Maintenance{" "}
              {!isAllowedFeature(store, "maintenance-portal") ? <Icon name="fa-lock" /> : null}
            </div>
          ),
          icon: "fa-gears",
          access: (store: any) =>
            utils.isUserSuperAdminOrAdmin(store) ||
            isUserSales(store) ||
            utils.isUserAccountAdmin(store) ||
            utils.isUserAccountUser(store) ||
            // Scoped by the API to their own properties.
            utils.isUserAgencyLandlord(store) ||
            utils.isUserTenant(store),
          path: "/maintenance"
        },
        {
          label: (
            <div>
              Expenses{" "}
              {!isAllowedFeature(store, "expenses-portal") ? <Icon name="fa-lock" /> : null}
            </div>
          ),
          icon: "fa-sticky-note",
          access: (store: any) =>
            utils.isUserSuperAdminOrAdmin(store) ||
            isUserSales(store) ||
            utils.isUserAccountAdmin(store) ||
            utils.isUserAccountUser(store) ||
            // Scoped by the API to their own properties.
            utils.isUserAgencyLandlord(store),
          path: "/expenses"
        },
        {
          label: "Documents",
          icon: "fa-file",
          access: false,
          // access: (store: any) =>
          //   utils.isUserSuperAdminOrAdmin(store) ||
          //   utils.isUserTenant(store) ||
          //   utils.isUserAccountAdmin(store) ||
          //   utils.isUserAccountUser(store),
          path: "/documents"
        },
        {
          label: "Reports",
          icon: "fa-file-word",
          access: (store: any) => utils.isUserSuperAdminOrAdmin(store) || isUserSales(store),
          path: "/reports"
        },
        {
          label: "Error Logs",
          icon: "fa-code",
          access: (store: any) => utils.isUserSuperAdminOrAdmin(store),
          path: `/error-logs`
        },
        {
          label: "Event Logs",
          icon: "fa-file",
          access: (store: any) => utils.isUserSuperAdminOrAdmin(store),
          path: `/event-logs`
        },
        {
          label: "Support",
          icon: "fa-headset",
          access: (store: any) =>
            utils.isUserSuperAdminOrAdmin(store) ||
            isUserSales(store) ||
            utils.isUserAccountAdmin(store) ||
            utils.isUserAccountUser(store) ||
            // Scoped by the API to their own properties.
            utils.isUserAgencyLandlord(store) ||
            utils.isUserTenant(store),
          path: "/support"
        },
        {
          label: "Create Invoice",
          icon: "fa-FilePdf",
          access: (store: any) => utils.isUserSuperAdmin(store),
          path: "/create-invoice"
        }
      ]
    }
  ];

  function setActiveDropdownToggle(label: string) {
    setActiveDropdown(label);
    const storeClone = { ...store };
    const uiSettings: any = storeClone.uiSettings ?? {};
    uiSettings.activeDropdown = label;
    storeClone.uiSettings = uiSettings;
    setStore(storeClone);
  }

  const activeClass = isDark
    ? "bg-white/10 border-l-2 border-primary text-white font-semibold"
    : "bg-primary/10 border-l-2 border-primary text-primary font-semibold";
  const inactiveClass = isDark
    ? "border-l-2 border-transparent text-white/60 hover:bg-white/10 hover:text-white"
    : "border-l-2 border-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-800";
  const itemBase =
    "flex cursor-pointer items-center truncate rounded-r-md px-5 py-2 text-[0.82rem] outline-none transition-all duration-200";

  function renderMenuNavItem(item: any) {
    const isLinkActive = window.location.pathname.indexOf(item.path) >= 0;
    const hasAccess = typeof item.access === "boolean" ? item.access : item.access(store);
    if (!hasAccess) return null;

    if (item.submenus && item.submenus.length > 0) {
      const isDropdownOpen = activeDropdown === item.label;
      return (
        <li key={item.path} className="relative" title={item.label}>
          <div
            onClick={() =>
              setActiveDropdownToggle(activeDropdown === item.label ? null : item.label)
            }
            className={`${itemBase} ${isDropdownOpen ? (isDark ? "text-white" : "text-gray-800") : isDark ? "text-white/60 hover:bg-white/10 hover:text-white" : "text-gray-500 hover:bg-gray-100 hover:text-gray-800"} flex justify-between border-l-2 border-transparent`}
          >
            <div className="flex items-center gap-3">
              <Icon name={item.icon} className="text-primary" size={isSidebarOpen ? "lg" : "xl"} />
              {isSidebarOpen && <span>{item.label}</span>}
            </div>
            {isSidebarOpen && (
              <Icon
                name={isDropdownOpen ? "fa-angle-up" : "fa-angle-down"}
                className={isDark ? "text-white/40" : "text-gray-400"}
              />
            )}
          </div>
          {isDropdownOpen && (
            <ul className="ml-4 mt-1 space-y-0.5">
              {item.submenus.map((submenu: any) => {
                const isSubActive = window.location.pathname.startsWith(submenu.path);
                return (
                  <li key={submenu.path}>
                    <a
                      onClick={() => navigate(submenu.path)}
                      className={`${itemBase} ${isSubActive ? activeClass : inactiveClass} gap-2`}
                    >
                      {submenu.icon && (
                        <Icon name={submenu.icon} className="text-primary" size="sm" />
                      )}
                      <span>{submenu.label}</span>
                    </a>
                  </li>
                );
              })}
            </ul>
          )}
        </li>
      );
    } else {
      return (
        <li key={item.path} className="relative" title={item.label}>
          <a
            className={`${itemBase} ${isLinkActive ? activeClass : inactiveClass} gap-3`}
            onClick={() => navigate(item.path)}
          >
            <Icon name={item.icon} className="text-primary" size={isSidebarOpen ? "lg" : "xl"} />
            <span className={!isSidebarOpen ? "hidden" : ""}>{item.label}</span>
          </a>
        </li>
      );
    }
  }

  function render() {
    return (
      <div
        className={`w-${isSidebarOpen ? "64" : "20"} fixed h-screen overflow-y-auto transition-colors ${isDark ? "bg-primary-dark" : "bg-white border-r border-gray-100"}`}
      >
        <nav
          id="sidenav-8"
          className={`w-${
            isSidebarOpen ? "64" : "20"
          } absolute left-0 top-0 z-[1035] h-full -translate-x-full overflow-y-auto overflow-x-hidden transition-colors data-[te-sidenav-hidden='false']:translate-x-0 ${isDark ? "bg-primary-dark" : "bg-white"}`}
          data-te-sidenav-hidden="false"
          data-te-sidenav-position="absolute"
          data-te-sidenav-accordion="true"
        >
          {/* Logo */}
          <a
            className="mb-2 flex cursor-pointer items-center justify-center gap-2 px-4 py-3 outline-none"
            onClick={() => navigate("/dashboard")}
          >
            <img
              src={process.env.PUBLIC_URL + "/img/logos/logo-2.png"}
              alt="Logo"
              className={!isSidebarOpen ? "w-10" : "w-9"}
            />
            <span
              className={`text-lg font-bold duration-300 ${!isSidebarOpen ? "hidden" : ""} ${isDark ? "text-white" : "text-gray-800"}`}
            >
              RentalEase
            </span>
          </a>

          {/* Divider — matches header bottom border */}
          <div className="mx-0 mb-3 border-t-2 border-primary" />

          <ul className="relative m-0 list-none px-1 pb-12" data-te-sidenav-menu-ref>
            {MenuGroups.map((group, groupIndex) => {
              const visibleItems = group.items.filter(item => {
                const hasAccess =
                  typeof item.access === "boolean" ? item.access : item.access(store);
                return hasAccess;
              });
              if (visibleItems.length === 0) return null;
              return (
                <li key={groupIndex} className="list-none">
                  {groupIndex > 0 && (
                    <div
                      className={`mx-4 my-2 border-t ${isDark ? "border-white/10" : "border-gray-100"}`}
                    />
                  )}
                  {group.title && isSidebarOpen && (
                    <p
                      className={`px-5 pb-1 pt-2 text-[0.62rem] font-semibold uppercase tracking-widest ${isDark ? "text-white/30" : "text-gray-400"}`}
                    >
                      {group.title}
                    </p>
                  )}
                  <ul className="list-none p-0">{group.items.map(renderMenuNavItem)}</ul>
                </li>
              );
            })}

            {/* Bottom divider */}
            <li className="px-4 pt-4">
              <div className={`border-t ${isDark ? "border-white/10" : "border-gray-200"}`} />
            </li>

            <li className="relative mt-1">
              <a
                className={`${itemBase} ${window.location.pathname === "/my-profile" ? activeClass : inactiveClass} gap-3`}
                onClick={() => navigate("/my-profile")}
              >
                <Icon name="fa-user" className="text-primary" size={isSidebarOpen ? "lg" : "xl"} />
                <span className={!isSidebarOpen ? "hidden" : ""}>My Profile</span>
              </a>
            </li>

            {!isUserTenant(store) && (
              <li className="relative">
                <a
                  className={`${itemBase} ${window.location.pathname === "/settings" ? activeClass : inactiveClass} gap-3`}
                  onClick={() => navigate("/settings")}
                >
                  <Icon
                    name="fa-gear"
                    className="text-primary"
                    size={isSidebarOpen ? "lg" : "xl"}
                  />
                  <span className={!isSidebarOpen ? "hidden" : ""}>Settings</span>
                </a>
              </li>
            )}

            {utils.isUserAccountAdmin(store) &&
              !utils.isEnterpriseOrPlusPlan(store) &&
              isSidebarOpen && (
                <li className="px-3 pt-3">
                  <button
                    onClick={() => navigate("/plans-upgrade")}
                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary/20 px-4 py-2 text-sm font-semibold text-primary transition-colors hover:bg-primary/30"
                  >
                    <Icon name="fa-star" className="text-primary" />
                    Upgrade plan
                  </button>
                </li>
              )}
          </ul>

          {isSidebarOpen && (
            <div
              className={`px-5 py-3 text-center text-xs ${isDark ? "text-white/20" : "text-gray-300"}`}
            >
              Powered by{" "}
              <a
                href="https://vertexease.com"
                target="_blank"
                rel="noreferrer"
                className="font-semibold hover:underline"
              >
                VertexEase
              </a>
              <div className="mt-1">version {packageJson.version}</div>
            </div>
          )}
        </nav>
      </div>
    );
  }
  return render();
}

export default SideNav;
